import {
  AccessGrant,
  AccessScope,
  AnyResource,
  CRUDAction,
  EntitlementType,
  OrgMap,
  SpikemarkAuthRole,
  SpikemarkResource,
  SpikemarkUser,
  UserAccessMap,
} from '@spikemark/auth';
import { PlayerRequest, TeamRequest, TournamentRequest } from '@spikemark/rest-api';
import { Session } from './client';
import { Entitlement } from '@spikemark/user-api';

type RoleAccessMap = Record<SpikemarkAuthRole, AccessTicket[]>;

type CRUDAccessTicket<T extends SpikemarkResource | AnyResource = AnyResource> = {
  resource: T;
  actions: CRUDAction[] | ['any'];
  restrict?: string[];
  refine?: never;
};

type ResourceActionMap = {
  [SpikemarkResource.Clubs]: CRUDAction;
  [SpikemarkResource.Players]: CRUDAction;
  [SpikemarkResource.Teams]: CRUDAction | 'roster';
  [SpikemarkResource.Tournaments]:
    | CRUDAction
    | 'enter'
    | 'withdraw'
    | 'save-results'
    | 'publish-results'
    | 'publish-feed';
  [SpikemarkResource.Users]: CRUDAction;
  [SpikemarkResource.CommitteeVisualisations]: 'read' | 'update' | 'any'; // Need update here to satisfy TypeScript. FIXME - why?
  [SpikemarkResource.Rankings]: 'read' | 'update' | 'any';
  [SpikemarkResource.Analytics]: 'read' | 'update' | 'any';
};

export type ResourceAction<R extends SpikemarkResource> = ResourceActionMap[R];

export type ModelAccessMappings = {
  [SpikemarkResource.Clubs]: {};
  [SpikemarkResource.Teams]: TeamRequest;
  [SpikemarkResource.Tournaments]: TournamentRequest;
  [SpikemarkResource.Players]: PlayerRequest;
  [SpikemarkResource.Users]: {};
  [SpikemarkResource.CommitteeVisualisations]: {};
  [SpikemarkResource.Rankings]: {};
  [SpikemarkResource.Analytics]: {};
};

type ModelAccessTicket<
  R extends SpikemarkResource,
  A extends string = ResourceAction<R>,
  T extends object = ModelAccessMappings[R],
> = {
  resource: R;
  actions: A[] | ['any'];
  restrict?: Partial<Record<A, (keyof T)[]>>;
  refine?: Partial<Record<A, (data: T) => boolean>>;
};

type AccessTicket = { scope: AccessScope } & (
  | CRUDAccessTicket
  | ModelAccessTicket<SpikemarkResource>
  | ModelAccessTicket<SpikemarkResource.Tournaments>
  | ModelAccessTicket<SpikemarkResource.Teams>
  | ModelAccessTicket<SpikemarkResource.CommitteeVisualisations>
);

const accessMap: RoleAccessMap = {
  [SpikemarkAuthRole.Scorers]: [],
  [SpikemarkAuthRole.Admins]: [
    {
      resource: 'any',
      actions: ['any'],
      scope: AccessScope.Any,
    },
  ],
  [SpikemarkAuthRole.Coaches]: [
    {
      resource: SpikemarkResource.Tournaments,
      actions: ['read', 'enter', 'withdraw'],
      scope: AccessScope.Any,
    },
    {
      resource: SpikemarkResource.Tournaments,
      actions: ['create', 'read', 'update', 'delete', 'save-results', 'publish-results'],
      scope: AccessScope.Own,
      refine: {
        update: (data: TournamentRequest) => !getIsExpiredTournament(data?.endDate),
        delete: (data: TournamentRequest) => !getIsExpiredTournament(data?.endDate),
      },
    },
    {
      resource: SpikemarkResource.Teams,
      actions: ['read'],
      scope: AccessScope.Any,
    },
    {
      resource: SpikemarkResource.Teams,
      actions: ['read', 'update'],
      scope: AccessScope.Own,
      restrict: {
        update: [
          'city',
          'schoolLogo',
          'state',
          'teamColour',
          'secondaryColour',
          'zip',
          'headCoach',
          'headCoachEmail',
          'assistantCoach',
          'assistantCoachEmail',
          'athleticDirector',
          'athleticDirectorEmail',
        ],
      },
    },
    {
      resource: SpikemarkResource.Players,
      actions: ['create', 'read', 'update', 'delete'],
      scope: AccessScope.Own,
    },
    {
      resource: SpikemarkResource.Rankings,
      actions: ['read'],
      scope: AccessScope.Any,
    },
    {
      resource: SpikemarkResource.Analytics,
      actions: ['read'],
      scope: AccessScope.Own,
    },
  ],
  [SpikemarkAuthRole.Committee]: [
    {
      resource: SpikemarkResource.CommitteeVisualisations,
      actions: ['read'],
      scope: AccessScope.Any,
    },
    {
      resource: SpikemarkResource.Rankings,
      actions: ['read'],
      scope: AccessScope.Any,
    },
  ],
  [SpikemarkAuthRole.Players]: [],
};

export const ACTIVE_ENTITLEMENT_STATUSES: ReadonlySet<Entitlement['status']> = new Set<
  Entitlement['status']
>(['ACTIVE', 'AVAILABLE', 'PENDING']);

/**
 * Extract team ID from data based on resource type
 */
function getTeamIdFromData<R extends SpikemarkResource>(
  resource: R,
  data?: ModelAccessMappings[R]
): string | undefined {
  if (!data) return undefined;

  switch (resource) {
    case SpikemarkResource.Tournaments:
      return (data as TournamentRequest).hostId ?? undefined;
    case SpikemarkResource.Teams:
      return (data as TeamRequest).schoolId ?? undefined;
    case SpikemarkResource.Players:
      return (data as PlayerRequest).schoolId ?? undefined;
    default:
      return undefined;
  }
}

/**
 * Get team-specific roles for a user and team
 */
function getTeamSpecificRoles(user: SpikemarkUser, teamId: string): SpikemarkAuthRole[] {
  const school = Object.values(user.schools ?? {}).find((school) => school.schoolId === teamId);
  return school ? [school.role] : [];
}

/**
 * Get default Coach role to access Admin portal if user is a Coach in any team
 */
function getDefaultRole(user: SpikemarkUser) {
  const isCoach = Object.values(user.schools ?? {}).some(
    (school) => school.role === SpikemarkAuthRole.Coaches
  );
  return isCoach ? [SpikemarkAuthRole.Coaches] : [];
}

/**
 * Determine access scope based on team ownership from data
 */
function getAccessScope<R extends SpikemarkResource>(
  user: SpikemarkUser | undefined,
  resource: R,
  data?: ModelAccessMappings[R]
): AccessScope {
  if (!user || !data) return AccessScope.Any;

  const teamId = getTeamIdFromData(resource, data);
  if (!teamId) return AccessScope.Any;

  // Check if user has any role for this team
  const hasTeamRole = Object.values(user.schools ?? {}).some(
    (school) => school.schoolId === teamId
  );
  return hasTeamRole ? AccessScope.Own : AccessScope.Any;
}

export function getAccessForRoles(roles: SpikemarkAuthRole[]): UserAccessMap {
  return roles.reduce<UserAccessMap>((acc, role) => {
    const tickets = accessMap[role];

    tickets.forEach(({ resource, actions, scope, restrict, refine }) => {
      actions.forEach((action) => {
        if (!acc[resource]) {
          acc[resource] = {};
        }

        const resourceMap = acc[resource]!;

        if (!resourceMap[action]) {
          resourceMap[action] = {};
        }

        resourceMap[action]![scope] = {
          granted: true,
          restrict: restrict?.[action],
          refine: refine?.[action],
        };
      });
    });

    return acc;
  }, {} as UserAccessMap);
}

/**
 * Get resource access map using team-specific roles when teamId is provided
 */
function getResourceAccessMapForUser<R extends SpikemarkResource>(
  user: SpikemarkUser,
  resource: R,
  teamId?: string
) {
  // Use team-specific roles when teamId is provided, otherwise fall back to Any scope permissions
  const roles = teamId ? getTeamSpecificRoles(user, teamId) : getDefaultRole(user);
  const access = user.isAdmin
    ? getAccessForRoles([SpikemarkAuthRole.Admins])
    : getAccessForRoles(roles);
  return access['any'] ?? access[resource];
}

/**
 * Enhanced resource access function that auto-detects scope and uses team-specific roles
 */
function getResourceAccessForUser<R extends SpikemarkResource>(
  user: SpikemarkUser,
  resource: R,
  action: ResourceActionMap[R],
  scope?: AccessScope,
  data?: ModelAccessMappings[R]
) {
  const grant: AccessGrant = {
    granted: false,
  };

  // Auto-detect scope from data if not provided
  const resolvedScope = scope ?? getAccessScope(user, resource, data);

  // Extract team ID for team-specific role lookup
  const teamId = getTeamIdFromData(resource, data);

  for (let i = resolvedScope; i <= AccessScope.Any; i++) {
    const accessMap = getResourceAccessMapForUser(user, resource, teamId);
    const anyGrant = accessMap?.['any']?.[i];
    const scopeGrant = anyGrant ?? accessMap?.[action]?.[i];

    if (scopeGrant?.granted) {
      grant.granted = true;

      if (scopeGrant.restrict) {
        grant.restrict = [...(grant.restrict ?? []), ...scopeGrant.restrict];
      }

      if (scopeGrant.refine && data) {
        grant.granted = scopeGrant.refine(data);
      }
    }
  }

  return grant;
}

export function getIsExpiredTournament(tournamentEndDate: Date): boolean {
  const lockDate = new Date(tournamentEndDate);
  lockDate.setDate(lockDate.getDate() + 14);
  return new Date() > lockDate;
}

export const Access = {
  any(user: SpikemarkUser, resource: SpikemarkResource): AccessGrant {
    return { granted: Boolean(user && getResourceAccessMapForUser(user, resource)) };
  },
  /**
   * Enhanced access checking with automatic scope detection and team-specific roles
   */
  can<R extends SpikemarkResource>(
    user: SpikemarkUser | undefined,
    resource: R,
    action: ResourceActionMap[R],
    scope?: AccessScope,
    data?: ModelAccessMappings[R]
  ): AccessGrant {
    return user
      ? getResourceAccessForUser(user, resource, action, scope, data)
      : {
          granted: false,
        };
  },
};

/**
 * Simple helper for display-only scope calculation
 * Use this only for UI purposes (badges, styling, etc.)
 * For access control, always use Access.can() which handles scope automatically
 */
export function getDisplayScope(
  user: SpikemarkUser | undefined,
  teamId: string | undefined
): AccessScope {
  if (!user || !teamId) return AccessScope.Any;
  return Object.values(user.schools ?? {}).some((school) => school.schoolId === teamId)
    ? AccessScope.Own
    : AccessScope.Any;
}

export const isAdmin = (userAuth: Session) => {
  return userAuth?.user.isAdmin ?? false;
};

/**
 * Check if the user has access to the entitlement provided,
 * Optionally against status - active, available
 */
export function getEntitlementAccessCheck(
  entitlements: Array<Entitlement>,
  entitlement: string,
  statuses: Array<Entitlement['status']> = Array.from(ACTIVE_ENTITLEMENT_STATUSES)
) {
  return entitlements.some((e) => e.entitlement === entitlement && statuses.includes(e.status));
}

export function canAccessSchool(
  schools: OrgMap | undefined = {},
  teamId: string,
  entitlementType: EntitlementType
) {
  return Object.values(schools).some(
    (school) =>
      school.schoolId === teamId && getEntitlementAccessCheck(school.entitlements, entitlementType)
  );
}
